import { Transition } from '@headlessui/react'
import { FormEventHandler, Fragment, useEffect, useState } from 'react'
import { MessageDescriptor, useIntl } from 'react-intl'

import useFeedback from '@/components/Feedback/useFeedback'
import Checkbox from '@/components/TailwindUI/Checkbox'
import cn from '@/utilities/cn'

import messages from './messages'

export enum SuggestionTypes {
  dataSource = 'dataSource',
  dataTransformUtility = 'dataTransformUtility'
}
interface SuggestionBoxProps {
  isShowingModal: boolean
  suggestionType: SuggestionTypes
  setIsShowingModal: (arg: boolean) => void
  labelText: MessageDescriptor
}

const SuggestionBox = ({ isShowingModal, suggestionType, setIsShowingModal, labelText }: SuggestionBoxProps) => {
  const intl = useIntl()

  const [hasCredentialsChecked, setHasCredentialsChecked] = useState(false)
  const [hasSandboxAccessChecked, setHasSandboxAccessChecked] = useState(false)
  const [isContactCustomerChecked, setIsContactCustomerChecked] = useState(false)

  useEffect(() => {
    isShowingModal ? setIsShowingModal(true) : setIsShowingModal(false)
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isShowingModal])

  const { submit, isLoading } = useFeedback({
    onSuccess: () => setIsShowingModal(false)
  })

  const onHasCredentialsCheckboxChange = (value: boolean) => {
    setHasCredentialsChecked(value)
  }

  const onHasSandboxAccess = (value: boolean) => {
    setHasSandboxAccessChecked(value)
  }

  const onContactCustomerCheckboxChange = (value: boolean) => {
    setIsContactCustomerChecked(value)
  }

  const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault()

    const data = new FormData(event.currentTarget)

    submit({
      message: data.get('comment')?.toString() || '_No comments_',
      data: {
        [suggestionType]: {
          value: data.get('comment')?.toString() || '_No comments_',
          display: data.get('comment')?.toString() || '_No comments_'
        },
        category: {
          value: `workshop-${suggestionType}-suggestion`,
          display: `:label: workshop-${suggestionType}-suggestion`
        },
        ...(suggestionType === SuggestionTypes.dataSource && {
          hasCredentials: {
            value: hasCredentialsChecked
              ? 'Customer has access to keys and credentials for this data source.'
              : 'Customer does not have access to keys and credentials for this data source.',
            display: hasCredentialsChecked
              ? ':key: Customer has access to keys and credentials for this data source.'
              : ':key: Customer does not have access to keys and credentials for this data source.'
          },
          hasSandboxAccess: {
            value: hasSandboxAccessChecked
              ? 'Customer has access to a sandbox for this data source.'
              : 'Customer does not have access to a sandbox for this data source.',
            display: hasSandboxAccessChecked
              ? ':sandcastle: Customer has access to a sandbox for this data source.'
              : ':sandcastle: Customer does not have access to a sandbox for this data source.'
          },
          contactCustomer: {
            value: isContactCustomerChecked
              ? 'Contact customer to setup this data source.'
              : 'Customer does not need contacted for this datasource to be setup.',
            display: isContactCustomerChecked
              ? ':wrench: Contact customer to setup this data source.'
              : ':wrench: Customer does not need contacted for this datasource to be setup.'
          }
        })
      }
    })
  }

  return (
    <Transition
      show={isShowingModal}
      as={Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="pointer-events-auto w-full">
        <form onSubmit={handleSubmit}>
          <div className="flex items-start">
            <div className="w-full flex-1 pt-0.5">
              <p className="mb-1 text-sm font-bold text-gray-900">
                {intl.formatMessage(labelText)}
                {'*'}
              </p>
              <div className={suggestionType === SuggestionTypes.dataTransformUtility ? 'mt-1' : 'mt-1 flex flex-col'}>
                <textarea
                  rows={1}
                  name="comment"
                  id="comment"
                  className="mr-4 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  defaultValue={''}
                  disabled={isLoading}
                  required
                  placeholder={intl.formatMessage(messages.suggestionPlaceholder, {
                    suggestionType: suggestionType
                      .replaceAll('dataTransformUtility', 'actions')
                      .replaceAll(/([A-Z])/g, ' $1')
                      .toLowerCase()
                  })}
                />
                {suggestionType === SuggestionTypes.dataTransformUtility && (
                  <p className="whitespace-pre-line py-3 text-xs text-gray-500">
                    {intl.formatMessage(messages.disclaimer)}
                  </p>
                )}
                {suggestionType === SuggestionTypes.dataSource && (
                  <div className="pt-3 pb-3">
                    <Checkbox
                      id="hasCredentials"
                      onChange={value => onHasCredentialsCheckboxChange(value.target.checked)}
                      checked={hasCredentialsChecked}
                      label="I have access to keys and credentials for this data source."
                    />
                    <Checkbox
                      id="hasSandboxAccess"
                      onChange={value => onHasSandboxAccess(value.target.checked)}
                      checked={hasSandboxAccessChecked}
                      label="I have access to a sandbox for this data source."
                    />
                    <Checkbox
                      id="contactCustomer"
                      onChange={value => onContactCustomerCheckboxChange(value.target.checked)}
                      checked={isContactCustomerChecked}
                      label="Please contact me to set up this data source."
                    />
                  </div>
                )}
                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={cn(
                      'inline-flex min-w-[142px] justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700',
                      { 'pointer-events-none opacity-60': isLoading }
                    )}
                  >
                    {intl.formatMessage(messages.sendToWorkgrid)}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {suggestionType === SuggestionTypes.dataSource && (
            <p className="whitespace-pre-line pt-3 text-xs text-gray-500">{intl.formatMessage(messages.disclaimer)}</p>
          )}
        </form>
      </div>
    </Transition>
  )
}

export default SuggestionBox
